import type { FontModel } from '@sosocio/creator-types/app';
import type { PageObjectModel } from '@sosocio/creator-types/project';
import {
	createDomElement,
	domElementToHTMLElement,
	getTextHorizontalLayout,
	getTextVerticalLayout,
	insertSoftHyphens,
	parseTextContentAlignment,
	stripHtmlEntities,
	textareaToDomElementText,
} from './dom';
import {
	getEffectiveLineHeight,
	getFontFamily,
	getTextRenderingFontSize,
	getTextRenderingLineHeight,
	isTextObjectFontSizeSource,
} from './font';

export interface CreatePageObjectTextSVGOptions {
	fontModel?: FontModel;
	objectModel: PageObjectModel;
}

/**
 * Constructs an SVG element for one current-format text page object.
 *
 * @param options Text object and optional loaded font metrics used for layout.
 * @returns Detached SVG element matching Creator's current text rendering.
 */
export function createTextSVGElement(options: CreatePageObjectTextSVGOptions): SVGSVGElement {
	const {
		fontModel,
		objectModel,
	} = options;

	if (!isTextObjectFontSizeSource(objectModel)) {
		throw new Error('Missing required text object font size');
	}

	const fontSize = getTextRenderingFontSize(objectModel);
	const fontFamily = getFontFamily({
		family: objectModel.fontface!,
		bold: !!objectModel.fontbold,
		italic: !!objectModel.fontitalic,
	});
	const effectiveLineHeight = getEffectiveLineHeight({
		fontFamily,
		fontModel,
		fontSize,
		isBold: !!objectModel.fontbold,
	});
	const lineHeight = getTextRenderingLineHeight({
		fallbackLineHeight: effectiveLineHeight,
		positionAttributes: objectModel.positionAttributes,
	});
	const textAlignContent = (
		parseTextContentAlignment(objectModel.positionAttributes?.['align-content'])
		?? 'start'
	);
	const horizontalLayout = getTextHorizontalLayout({
		fontFamily,
		fontSize,
		maxWidth: objectModel.width,
		positionAttributes: objectModel.positionAttributes,
		text: objectModel.text!,
	});
	const verticalLayout = getTextVerticalLayout({
		positionAttributes: objectModel.positionAttributes,
	});
	const svgElement = document.createElementNS(
		'http://www.w3.org/2000/svg',
		'svg',
	);

	svgElement.setAttribute(
		'height',
		String(objectModel.height),
	);
	svgElement.setAttribute(
		'width',
		String(objectModel.width),
	);
	svgElement.setAttribute(
		'viewBox',
		`0 0 ${objectModel.width} ${objectModel.height}`,
	);
	/**
	 * Contextual alternates can change when every SVG line becomes a separate
	 * shaping run. Disable them here so SVG and textarea glyphs stay aligned.
	 */
	svgElement.setAttribute(
		'style',
		'font-variant-ligatures: no-contextual; font-feature-settings: \'calt\' 0, \'rand\' 0;',
	);

	if (objectModel.positionAttributes) {
		const { positionAttributes } = objectModel;

		svgElement.setAttribute(
			'style',
			Object
				.keys(positionAttributes)
				.filter((attributeKey) => (
					attributeKey !== 'align-content'
					&& attributeKey !== 'line-height'
					&& attributeKey !== 'padding-bottom'
					&& attributeKey !== 'padding-top'
				))
				.reduce(
					(
						styles,
						attributeKey,
					) => `${styles} ${attributeKey}: ${positionAttributes[attributeKey]};`,
					svgElement.getAttribute('style')!,
				),
		);
	}

	if (objectModel.bgcolor) {
		svgElement.appendChild(
			domElementToHTMLElement(
				createDomElement(
					'rect',
					{
						x: 0,
						y: 0,
						fill: objectModel.bgcolor,
						height: objectModel.height,
						width: objectModel.width,
					},
					undefined,
					'http://www.w3.org/2000/svg',
				),
			),
		);
	}

	svgElement.appendChild(
		domElementToHTMLElement(
			textareaToDomElementText({
				alignContent: textAlignContent,
				fill: objectModel.fontcolor!,
				fontFamily,
				fontSize,
				horizontalLayout,
				lineHeight,
				maxHeight: objectModel.height,
				maxWidth: objectModel.width,
				positionAttributes: objectModel.positionAttributes,
				text: insertSoftHyphens({
					fontFamily,
					fontSize,
					maxWidth: horizontalLayout.contentWidth,
					positionAttributes: objectModel.positionAttributes,
					text: objectModel.text!,
				}),
				textAlign: objectModel.align!.toLowerCase() as Lowercase<NonNullable<PageObjectModel['align']>>,
				verticalLayout,
			}),
		),
	);

	return svgElement;
}

/**
 * Serializes a current-format text page object for canvas and PDF rendering.
 *
 * @param options Text object and optional loaded font metrics used for layout.
 * @returns SVG markup without HTML entities unsupported by PDF generation.
 */
export function createTextSVGString(options: CreatePageObjectTextSVGOptions): string {
	const svgElement = createTextSVGElement(options);
	const svgString = stripHtmlEntities(new XMLSerializer().serializeToString(svgElement));

	svgElement.remove();

	return svgString;
}
